'use client';
/**
 * Mes présences — ce que J'AI badgé et ce qui a été validé (RQ, 30/09/2026 :
 * « rajoute-moi dans mon compte, comme Mon planning, mes présences »).
 *
 * Le pendant de « Mon planning » pour le pointage. La route
 * `ged/rh/mes-presences` filtre côté serveur sur l'orgId du porteur du jeton :
 * l'écran ne choisit pas de qui il parle, il ne peut pas.
 *
 * Le relevé du mois est EXACTEMENT celui que la RH imprime pour l'entretien
 * (`FicheReleve`, partagé) : la personne lit les mêmes faits que son
 * responsable, sans le bouton « Corriger ». Rien ne se modifie d'ici — une
 * journée fausse se signale à la RH, qui la corrige avec un motif.
 */
import React from 'react';
import { I, card, EmptyState, Skeleton, TitrePage } from '@/components/ui';
import { heuresLisibles as h, fmtDateHeure, fmtDate } from '@/lib/meta';
import { FicheReleve, LIB_ANOMALIE, type Detail } from '@/components/screens/RhPresences';

import { Bouton } from '@/shared/Bouton';
import { libelleMois, libelleJourCourt } from '@/lib/jour';
import { Tableau } from '@/shared/Tableau';
type Journee = {
  date: string; etat: string; anomalies: string[];
  heuresPointees: number | null; heuresPlanifiees: number | null; heuresRetenues: number | null;
  segments: { debut: string | null; fin: string | null; poste: string }[];
  corrige: { motif: string; le: string } | null;
  validee: boolean; valideeLe: string;
};
type Reponse = Detail & { journees: Journee[] };

const heure = (ts: string | null) => (ts ? ts.slice(11, 16) : '—');
const jourLong = (iso: string) => libelleJourCourt(iso);
const dateCourte = (ts: string) => (ts ? fmtDateHeure(ts, false) : '');
function moisDecale(mois: string, n: number): string {
  const [a, m] = mois.split('-').map(Number);
  const t = a * 12 + (m - 1) + n;
  return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, '0')}`;
}

/** Ce que la journée est devenue, en mots de tous les jours. */
function suite(j: Journee): { texte: string; couleur: string } {
  if (j.validee) return { texte: `✓ validée${j.valideeLe ? ` le ${dateCourte(j.valideeLe)}` : ''}`, couleur: 'var(--green-600)' };
  if (j.corrige) return { texte: 'corrigée par la RH, validation à venir', couleur: 'var(--text-body)' };
  if (j.etat === 'anomalie') return { texte: 'pointage incomplet — en attente de la RH', couleur: 'var(--amber-600)' };
  if (j.etat === 'non_mesure') return { texte: 'aucun passage lu', couleur: 'var(--text-muted)' };
  return { texte: 'en attente de validation', couleur: 'var(--text-muted)' };
}

export function MesPresences() {
  const [mois, setMois] = React.useState(() => new Date().toISOString().slice(0, 7));
  const [d, setD] = React.useState<Reponse | null>(null);
  const [erreur, setErreur] = React.useState('');
  React.useEffect(() => {
    let vivant = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- efface le mois précédent pendant la requête (même raison que Mon planning)
    setD(null); setErreur('');
    void fetch(`/api/ged/ged/rh/mes-presences?mois=${mois}`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 404) throw new Error('Aucun dossier du personnel n’est rattaché à votre compte.');
        if (!r.ok) throw new Error('Présences momentanément illisibles — réessayez dans un instant.');
        return r.json();
      })
      .then((x) => { if (vivant) setD(x as Reponse); })
      .catch((e: Error) => { if (vivant) setErreur(e.message); });
    return () => { vivant = false; };
  }, [mois]);

  const [a, m] = mois.split('-').map(Number);
  const libMois = libelleMois(`${a}-${String(m).padStart(2, '0')}`);

  return (
    <section>
      <TitrePage eyebrow="Mon espace" titre="Mes présences"
        sousTitre={<>Vos passages à la badgeuse, les heures retenues et ce que la RH a validé. Vous ne
          voyez ici que votre propre pointage. Une journée vous semble fausse ? Signalez-la au
          responsable RH, qui la corrige avec un motif.</>} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0', flexWrap: 'wrap' }}>
        <Bouton taille="sm" aria-label="Mois précédent" onClick={() => setMois(moisDecale(mois, -1))}><I n="chevron-left" s={14} /></Bouton>
        <strong style={{ fontSize: 14, minWidth: 150, textAlign: 'center' }}>{libMois}</strong>
        <Bouton taille="sm" aria-label="Mois suivant" onClick={() => setMois(moisDecale(mois, 1))}><I n="chevron-right" s={14} /></Bouton>
        <Bouton taille="sm" onClick={() => setMois(new Date().toISOString().slice(0, 7))}>Ce mois-ci</Bouton>
      </div>

      {erreur ? <EmptyState titre={erreur} /> : !d ? <Skeleton rows={6} /> : (
        <>
          <div style={{ ...card, padding: 16, marginBottom: 14 }}>
            <FicheReleve d={d} libMois={libMois} />
          </div>

          <div style={{ ...card, padding: 16 }}>
            <h3 style={{ fontSize: 13.5, margin: '0 0 6px' }}>Jour par jour</h3>
            {/* L10 (11/10/2026) : le relevé jour par jour est un registre SIMPLE sur le socle. */}
            <Tableau
              id="mes-presences-jours"
              niveau="simple"
              densite="compacte"
              lignes={d.journees}
              cleLigne={(j) => j.date}
              vide={{ titre: 'Aucune journée de pointage enregistrée pour ce mois.' }}
              colonnes={[
                { cle: 'date', libelle: 'Jour', type: 'date', valeur: (j) => j.date, style: { whiteSpace: 'nowrap' }, rendu: (j) => jourLong(j.date) || fmtDate(j.date) },
                {
                  cle: 'badge', libelle: 'Badgé (entrée → sortie)', valeur: (j) => j.segments.map((x) => `${heure(x.debut)} → ${heure(x.fin)}`).join(' ; '),
                  rendu: (j) => (j.segments.length ? j.segments.map((x, i) => (
                    <div key={i} style={{ whiteSpace: 'nowrap' }}>{heure(x.debut)} → {heure(x.fin)}{x.poste ? <span style={{ color: 'var(--text-muted)' }}> · {x.poste}</span> : null}</div>
                  )) : <span style={{ color: 'var(--text-muted)' }}>aucun passage</span>),
                },
                { cle: 'prevu', libelle: 'Prévu', type: 'nombre', valeur: (j) => j.heuresPlanifiees ?? null, rendu: (j) => (j.heuresPlanifiees != null ? h(j.heuresPlanifiees) : '—') },
                { cle: 'retenu', libelle: 'Retenu', type: 'nombre', valeur: (j) => j.heuresRetenues ?? null, style: { fontWeight: 600 }, rendu: (j) => (j.heuresRetenues != null ? h(j.heuresRetenues) : '—') },
                {
                  cle: 'remarques', libelle: 'Remarques', valeur: (j) => j.anomalies.map((c) => LIB_ANOMALIE[c] || c).join(' · '),
                  rendu: (j) => (
                    <>
                      {j.anomalies.map((c) => LIB_ANOMALIE[c] || c).join(' · ')}
                      {j.corrige?.motif && <div style={{ color: 'var(--text-muted)' }}>Correction : {j.corrige.motif}</div>}
                    </>
                  ),
                },
                { cle: 'suite', libelle: 'Suite', type: 'liste', valeur: (j) => suite(j).texte, rendu: (j) => { const s = suite(j); return <span style={{ color: s.couleur, fontWeight: j.validee ? 600 : 400 }}>{s.texte}</span>; } },
              ]}
            />
          </div>
        </>
      )}
    </section>
  );
}
